import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { readNeoOffice } from './api';
import Cm1Archive, { InitialCustomer } from './Cm1Archive';
import './neo-office.css';

type History = {
  run: number | null;
  records: { id: number; customId: string; name: string | null }[];
  total: number;
  size: number;
};

export default function CustomerCm1History({
  customerType,
  customerId,
}: {
  customerType: string;
  customerId: string;
}) {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<
    (InitialCustomer & { revision: number }) | null
  >(null);
  const history = useQuery({
    queryKey: ['neo', 'cm1-live-customer', customerType, customerId, page],
    queryFn: () =>
      readNeoOffice<History>(
        `v1/neo-office/cm1/live-customers/${encodeURIComponent(
          customerType
        )}/${encodeURIComponent(customerId)}?page=${page}`
      ),
    retry: false,
  });
  return (
    <section
      className="neo neo-customer-history"
      aria-label="Customer CM1 history"
    >
      <h2>Historical CM1 data</h2>
      <p>
        Historical comments, case handling, alerts, documents and profiles
        linked by exact customer references.
      </p>
      {history.isPending && <p role="status">Loading customer history…</p>}
      {history.isError && (
        <p role="alert">
          Unable to load this customer&apos;s CM1 history.{' '}
          {history.error.message}
        </p>
      )}
      {history.data && !history.data.run && (
        <p>No historical archive has been published yet.</p>
      )}
      {history.data?.run && !history.data.total ? (
        <p>
          No exact CM1 customer link was found. Unresolved records are still
          available in the{' '}
          <a href="/fcu/cm1-archive" target="_blank" rel="noopener noreferrer">
            CM1 archive ↗
          </a>
          .
        </p>
      ) : null}
      {history.data?.records.map((record) => (
        <article key={record.id}>
          <strong>{record.name ?? record.customId}</strong>
          <p>{record.customId}</p>
          <nav
            className="neo-cm1-tabs"
            aria-label={`History for ${record.customId}`}
          >
            {[
              ['timeline', 'Case history & comments'],
              ['notes', 'Additional notes'],
              ['cases', 'Alerts and cases'],
              ['documents', 'Documents'],
              ['profiles', 'Full history'],
            ].map(([section, label]) => (
              <button
                key={section}
                onClick={() => {
                  const run = history.data.run;
                  if (run)
                    setSelected((previous) => ({
                      id: record.id,
                      run,
                      section,
                      revision: (previous?.revision ?? 0) + 1,
                    }));
                }}
              >
                {label}
              </button>
            ))}
          </nav>
        </article>
      ))}
      {history.data && history.data.total > history.data.size && (
        <div className="neo-pager">
          <button
            disabled={page === 1 || history.isFetching}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous customers
          </button>
          <span>Page {page}</span>
          <button
            disabled={
              page * history.data.size >= history.data.total ||
              history.isFetching
            }
            onClick={() => setPage((p) => p + 1)}
          >
            Next customers
          </button>
        </div>
      )}
      {selected && (
        <>
          <button onClick={() => setSelected(null)}>
            Close historical details
          </button>
          <Cm1Archive
            key={`${selected.run}-${selected.id}-${selected.revision}`}
            initialCustomer={selected}
          />
        </>
      )}
    </section>
  );
}
